export interface UrlParameters {
  code?: string;
  userCode?: string;
  error?: string;
  errorDescription?: string;
}

export interface HashParameters {
  accessToken?: string;
  idToken?: string;
  tokenType?: string;
  expiresIn?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

export class UrlParsingService {
  static parseUrlParameters(url?: string): UrlParameters {
    const urlToParse = url || window.location.search;
    const params = new URLSearchParams(urlToParse);
    
    return {
      code: params.get('code') || undefined,
      userCode: params.get('user_code') || undefined,
      error: params.get('error') || undefined,
      errorDescription: params.get('error_description') || undefined,
    };
  }

  static parseHashParameters(hash?: string): HashParameters {
    const hashToParse = hash || window.location.hash;
    
    if (!hashToParse || !hashToParse.startsWith('#')) {
      return {};
    }

    const params = new URLSearchParams(hashToParse.substring(1));
    
    return {
      accessToken: params.get('access_token') || undefined,
      idToken: params.get('id_token') || undefined,
      tokenType: params.get('token_type') || undefined,
      expiresIn: params.get('expires_in') || undefined,
      state: params.get('state') || undefined,
      error: params.get('error') || undefined,
      errorDescription: params.get('error_description') || undefined,
    };
  }

  static hasUrlParameters(url?: string): boolean {
    const params = this.parseUrlParameters(url);
    return Object.keys(params).some(key => params[key as keyof UrlParameters] !== undefined);
  }

  static hasHashParameters(hash?: string): boolean {
    const params = this.parseHashParameters(hash);
    return Object.keys(params).some(key => params[key as keyof HashParameters] !== undefined);
  }

  static getDeviceCodeFromUrl(url?: string): string | null {
    const params = this.parseUrlParameters(url);
    return params.code || params.userCode || null;
  }

  static hasAuthenticationError(url?: string, hash?: string): { error: string; description?: string } | null {
    const urlParams = this.parseUrlParameters(url);
    const hashParams = this.parseHashParameters(hash);
    
    const error = urlParams.error || hashParams.error;
    const description = urlParams.errorDescription || hashParams.errorDescription;
    
    if (error) {
      return { error, description };
    }
    
    return null;
  }

  static clearUrlParameters(): void {
    if (window.history && window.history.replaceState) {
      const url = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, url);
    }
  }

  static clearHashParameters(): void {
    if (window.history && window.history.replaceState) {
      const url = window.location.origin + window.location.pathname + window.location.search;
      window.history.replaceState({}, document.title, url);
    }
  }

  static clearAllParameters(): void {
    if (window.history && window.history.replaceState) {
      const url = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, url);
    }
  }

  static validateUserCode(code: string): boolean {
    // Azure AD user codes are typically 8-12 characters, alphanumeric
    return /^[A-Z0-9]{6,12}$/i.test(code.trim());
  }

  static formatUserCode(code: string): string {
    return code.trim().toUpperCase();
  }
}